import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Typography, Avatar, Dropdown, Drawer } from 'antd';
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  UserOutlined,
  LogoutOutlined,
  BellOutlined,
  MessageOutlined,
  MenuOutlined
} from '@ant-design/icons';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NegativeBalanceWarning from '../components/NegativeBalanceWarning';
import axios from 'axios';
import { API_URL } from '../config';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

const DashboardLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [userProfile, setUserProfile] = useState(null);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setMobileDrawerVisible(false); // Close drawer when screen becomes larger
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Fetch user profile on mount to get balance information
  useEffect(() => {
    if (currentUser) {
      const fetchUserProfile = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API_URL}/api/v1/user/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.status === 200 && response.data) {
            setUserProfile(response.data);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      };
      
      fetchUserProfile();
    }
  }, [currentUser]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  
  const handleRecharge = () => {
    // Navigate to recharge page
    navigate('/profile/topup');
  };

  const userMenu = (
    <Menu>
      <Menu.Item key="profile" icon={<UserOutlined />}>
        <Link to="/profile">个人资料</Link>
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
        退出登录
      </Menu.Item>
    </Menu>
  );

  // Check if user is admin to show admin menu
  const isAdmin = currentUser?.role === 'admin';

  // Determine current active menu key based on location
  const getCurrentMenuKey = () => {
    const path = location.pathname;
    if (path.includes('/accounts')) return 'accounts';
    if (path.includes('/messages')) return 'messages';
    if (path.includes('/admin/users')) return 'admin-users';
    return 'accounts';
  };

  // Menu content to be used in both Sider and mobile Drawer
  const menuContent = (
    <Menu 
      theme="light" 
      mode="inline" 
      selectedKeys={[getCurrentMenuKey()]}
    >
      <Menu.Item key="accounts" icon={<UserOutlined />}>
        <Link to="/accounts">账户</Link>
      </Menu.Item>
      <Menu.Item key="messages" icon={<MessageOutlined />}>
        <Link to="/messages">消息</Link>
      </Menu.Item>
      
      {/* Admin menu items */}
      {isAdmin && (
        <>
          <Menu.Divider />
          <Menu.ItemGroup title="管理员">
            <Menu.Item key="admin-users" icon={<UserOutlined />}>
              <Link to="/admin/users">用户</Link>
            </Menu.Item>
          </Menu.ItemGroup>
        </>
      )}
    </Menu>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sidebar - hidden on mobile */}
      {!isMobile && (
        <Sider 
          trigger={null} 
          collapsible 
          collapsed={collapsed}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            zIndex: 10,
          }}
        >
          <div className="logo" style={{ padding: '16px', textAlign: 'center' }}>
            <Link to="/">
              <Typography.Title level={4} style={{ color: '#1890ff', margin: 0 }}>
                SMS Bridge
              </Typography.Title>
            </Link>
          </div>
          {menuContent}
        </Sider>
      )}

      <Layout style={{ marginLeft: !isMobile ? (collapsed ? 80 : 200) : 0 }}>
        {/* Header with toggle and user info */}
        <Header style={{ 
          padding: '0 16px', 
          background: '#fff', 
          boxShadow: '0 1px 4px rgba(0,21,41,.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {/* Left side - Menu toggle */}
          <div>
            {!isMobile ? (
              <Button 
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
              />
            ) : (
              <Button 
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setMobileDrawerVisible(true)}
              />
            )}
          </div>
          
          {/* Right side - User info */}
          <div>
            <Dropdown overlay={userMenu} placement="bottomRight">
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <Avatar icon={<UserOutlined />} />
                <span style={{ marginLeft: 8 }}>{currentUser?.email}</span>
              </div>
            </Dropdown>
          </div>
        </Header>
        
        {/* Main content area */}
        <Content style={{ margin: '24px 16px', overflow: 'initial' }}>
          {/* Add NegativeBalanceWarning if user has negative balance */}
          {userProfile && userProfile.balance < 0 && (
            <NegativeBalanceWarning 
              balance={userProfile.balance} 
              onRecharge={handleRecharge}
            />
          )}
          
          {/* Main content from child routes */}
          <Outlet />
        </Content>
      </Layout>
      
      {/* Mobile drawer for navigation */}
      {isMobile && (
        <Drawer
          title="SMS Bridge"
          placement="left"
          closable={true}
          onClose={() => setMobileDrawerVisible(false)}
          visible={mobileDrawerVisible}
        >
          {menuContent}
        </Drawer>
      )}
    </Layout>
  );
};

export default DashboardLayout; 